import * as React from 'react';
import { ArrowDownRight, ArrowUpRight, type LucideIcon, Minus } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Skeleton, Tooltip } from '@/components/ui/misc';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export interface StatTileProps {
  label: string;
  value: React.ReactNode;
  /** Signed period-over-period change, already a percentage. */
  delta?: number | null;
  /** Whether an increase is a good thing — unpaid policies rising is not. */
  higherIsBetter?: boolean;
  icon?: LucideIcon;
  hint?: string;
  /** Escalates the tile when a threshold is crossed (spec §8.3). */
  tone?: 'default' | 'warning' | 'critical';
  isLoading?: boolean;
  onClick?: () => void;
  footer?: React.ReactNode;
}

export function StatTile({
  label,
  value,
  delta,
  higherIsBetter = true,
  icon: Icon,
  hint,
  tone = 'default',
  isLoading,
  onClick,
  footer,
}: StatTileProps) {
  const { t, percent } = useI18n();

  if (isLoading) {
    return (
      <Card className="h-full gap-2 p-4">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-2 h-7 w-32" />
        <Skeleton className="mt-2 h-3 w-20" />
      </Card>
    );
  }

  const isGood = delta == null ? null : higherIsBetter ? delta > 0 : delta < 0;
  const DeltaIcon = delta == null || delta === 0 ? Minus : delta > 0 ? ArrowUpRight : ArrowDownRight;

  const content = (
    <Card
      className={cn(
        // h-full: a tile wrapped in the <button> below must still stretch to the
        // grid row, otherwise clickable tiles sit shorter than static ones.
        'h-full gap-0 p-4.5',
        tone === 'warning' && 'border-serious/30 bg-serious/[0.04]',
        tone === 'critical' && 'border-critical/30 bg-critical/[0.04]',
        onClick && 'hover-lift hover:border-border cursor-pointer text-left',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-muted-soft text-[12.5px] font-medium">{label}</p>
        {Icon && (
          <Icon
            className={cn(
              'size-4 shrink-0',
              tone === 'critical'
                ? 'text-critical'
                : tone === 'warning'
                  ? 'text-serious'
                  : 'text-muted-soft opacity-60',
            )}
          />
        )}
      </div>

      {/* Proportional figures, not tabular: a standalone value at this size reads
          loose with every digit forced to the width of a zero. */}
      <p className="mt-2 text-[27px] leading-none font-semibold tracking-[-0.025em]">{value}</p>

      {(delta != null || footer) && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          {delta != null && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium',
                isGood === null || delta === 0
                  ? 'text-muted-foreground'
                  : isGood
                    ? 'text-good'
                    : 'text-critical',
              )}
            >
              <DeltaIcon className="size-3.5" />
              {percent(Math.abs(delta))}
            </span>
          )}
          {delta != null && <span className="text-muted-soft">{t('dashboard.vsPrevious')}</span>}
          {footer}
        </div>
      )}
    </Card>
  );

  const wrapped = onClick ? (
    <button type="button" onClick={onClick} className="h-full w-full text-left">
      {content}
    </button>
  ) : (
    content
  );

  return hint ? <Tooltip content={hint}>{wrapped}</Tooltip> : wrapped;
}

export function StatTileRow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn('grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-6', className)}
    >
      {children}
    </div>
  );
}
